import _ from 'lodash';
import { injectable, inject } from 'inversify';
import { MessageType, ChatMessageModel } from '@/components/chat/_domain';
import { SweetAlertOptions } from 'sweetalert2';
import { UserPreferences } from '@/app-modules/store/modules/user-preferences';
import { IAlertWordsService, IAlertWordsCheckOptions, IAlertWordsMessageFormatter } from '@/components/chat/_domain/modules/alert-words/alert-words-service-interface';
import { SERVICE_IDENTIFIERS } from '@/app-modules/_ioc/service-identifiers';
import { ISweetAlertService } from '@/app-modules/sweet-alert-service';
import { IPersistedStore } from '@/app-modules/store/persisted-store';
import { User } from '@/app-modules/store/modules/user';
import { WordTokeniser } from '@/components/chat/_domain/modules/alert-words/word-tokeniser';
import { Lang } from 'vuejs-localization';
import { ILocalisationService } from '@/app-modules/localisation-service/localisation-service';
import { ModalManagerPlugin } from '@/plugins/modal-manager';
import { container } from '@/app-modules/_ioc';
import { IModalManagerService } from '@/app-modules/modal-manager/modal-manager-service';

@injectable()
class AlertWordsService implements IAlertWordsService {
  private readonly $lang: Lang;

  constructor(
    @inject(SERVICE_IDENTIFIERS.ISweetAlertService) private readonly sweetAlertService: ISweetAlertService,
    @inject(SERVICE_IDENTIFIERS.IPersistedStore) private readonly persistedStore: IPersistedStore,
    @inject(SERVICE_IDENTIFIERS.ILocalisationService) private readonly localisationService: ILocalisationService,
    @inject(SERVICE_IDENTIFIERS.IAlertWordsMessageFormatter) private readonly alertWordsMessageFormatter: IAlertWordsMessageFormatter
    ) {
    this.$lang = this.localisationService.getLangInstance();
  }

  public checkAlertWords(alertWordsCheckOptions: IAlertWordsCheckOptions): Promise<void> {
    return new Promise((resolve, reject) => {
      const alertWords = this.getAlertWords();
      let messageText = this.cleanseHtmlFontTagsFromMessage(alertWordsCheckOptions.message);
      messageText = this.alertWordsMessageFormatter.formatMessage(messageText);
      const tokenisedMessage = this.tokeniseMessageIntoWords(messageText);
      const extractedAlertWords = this.extractAlertWordsFromTokenisedMessage(tokenisedMessage, alertWords);
      if (extractedAlertWords && extractedAlertWords.length > 0) {
        alertWordsCheckOptions.message.messageType = MessageType.AlertWord;
        const alertOptions = this.getSweetAlertOptions(alertWordsCheckOptions, extractedAlertWords);
        this.sweetAlertService.enqueue(alertOptions, true);
      }
      resolve();
    });
  }

  private getAlertWords(): string[] {
    const user = this.getUser();
    if (!user.cleansedAlertWords || user.cleansedAlertWords.length === 0) {
      const userPreferences = this.getUserPreferences();
      user.cleansedAlertWords = userPreferences.alertWords
                                               .filter((alertWord) => alertWord.length > 1)
                                               .map((alertWord) => this.removeWhitespaceAndConvertToLowercase(alertWord));
    }
    return user.cleansedAlertWords;
  }

  private getUserPreferences(): UserPreferences {
    return this.persistedStore.Get(UserPreferences);
  }

  private getUser(): User {
    return this.persistedStore.Get(User);
  }

  private cleanseHtmlFontTagsFromMessage(message: ChatMessageModel): string {
    const regexTagRemoval = /<FONT\b[^>]*>(.*?)<\/FONT>/gmi;
    const match = regexTagRemoval.exec(message.messageText);
    return match === null ? message.messageText : match[1];
  }

  private tokeniseMessageIntoWords(messageText: string): string[] {
    const wordTokeniser = new WordTokeniser((messageText || '').toLowerCase());
    const words = wordTokeniser.toWords();
    return words;
  }

  private removeWhitespaceAndConvertToLowercase(stringValue: string): string {
    return stringValue.replace(/\s/gm, '').toLowerCase();
  }

  private extractAlertWordsFromTokenisedMessage(tokenisedMessage: string[], alertWords: string[]): string[] {
    const extractedAlertWords: string[] = [];
    tokenisedMessage.forEach((token) => extractedAlertWords.push(...this.extractAlertWordsFromMessageToken(token, alertWords)));
    return extractedAlertWords.filter((alertWord, index) => extractedAlertWords.indexOf(alertWord) === index);
  }

  private extractAlertWordsFromMessageToken(messageToken: string, alertWords: string[]): string[] {
    return alertWords.filter((alertWord) => messageToken === alertWord);
  }

  private getSweetAlertOptions(alertWordsCheckOptions: IAlertWordsCheckOptions, extractedAlertWords: string[]): SweetAlertOptions {
    return {
      html: this.getSweetAlertMessageText(alertWordsCheckOptions, extractedAlertWords),
      toast: true,
      type: 'warning',
      position: 'top-right',
      allowOutsideClick: false,
      // TODO : TECHDEBT - To be completed from another ticket we open chat window and scroll window to position of alert.
      showConfirmButton: true,
      confirmButtonText: this.$lang.chat.alertWords.confirmButton,
      // TODO : TECHDEBT - Cancel will dismiss all alerts queued. Need to be able to dismiss all and current alert.
      showCancelButton: true,
      cancelButtonText: this.$lang.chat.alertWords.dismissButton,
      // TODO : TECHDEBT - This will dismiss all toast alerts after 30 seconds rather than the current alert.
      timer: 30000,
      preConfirm: () => {
        const ALERT_MESSAGE_TIMEOUT_MILLISECONDS = 10000;
        this.bringModalToFront(alertWordsCheckOptions.modalGuid);
        alertWordsCheckOptions.message.isHighlighted = true;
        window.setTimeout(() => alertWordsCheckOptions.message.isHighlighted = false, ALERT_MESSAGE_TIMEOUT_MILLISECONDS);
      }
    };
  }

  private getSweetAlertMessageText(alertWordsCheckOptions: IAlertWordsCheckOptions, extractedAlertWords: string[]): string {
    const alertTemplate: string = this.$lang.chat.alertWords.template;
    return alertTemplate.format({
      alertWords: extractedAlertWords.join(', '),
      username: alertWordsCheckOptions.message.username,
      chatRoom: alertWordsCheckOptions.chatRoomName ? alertWordsCheckOptions.chatRoomName : alertWordsCheckOptions.gameName
    });
  }

  private bringModalToFront(modalGuid: string): void {
    const modalManager = this.getModalManagerInstance();
    modalManager.bringToFront(modalGuid);
  }

  private getModalManagerInstance(): ModalManagerPlugin {
    const modalManagerService = container.get(SERVICE_IDENTIFIERS.IModalManagerService) as IModalManagerService;
    return modalManagerService.getModalManager();
  }
}

export { IAlertWordsService, AlertWordsService };



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/components/chat/_domain/modules/alert-words/alert-words-service.ts